import { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import AddProduct from "./components/addProduct";
import { Button } from "../../components/ui/button";
import Common from "../../helper/common";
import AdvanceTable from "../../components/advanceTable";
import { respEnum, SearchTypeEnum } from "../../constants/appConstant";
import {
  AppSelect,
  AppSearchSectionSelect,
} from "../../components/appComponents";
import Barcode from "react-barcode";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";

const Products = () => {
  const [data, setData] = useState({});
  const [categoryid, setCategoryid] = useState(null);
  const [isactive, setIsactive] = useState(true);
  const [isdisplayontop, setIsdisplayontop] = useState(null);
  const [isdisplayonapp, setIsdisplayonapp] = useState(null);
  const [open, setOpen] = useState(false);
  const [refId, setRefId] = useState(null);
  const [categoriesList, setCategoriesList] = useState([]);
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const [selectedBarcode, setSelectedBarcode] = useState(null);
  const [selectedProductName, setSelectedProductName] = useState("");
  const [barcodeQuantity, setBarcodeQuantity] = useState(1);
  const [didClickPrint, setDidClickPrint] = useState(false);

  const BARCODE_PER_PAGE = 10; // 2 columns x 5 rows
  const ROWS_PER_COLUMN = 5;
  const BARCODE_LABEL_W_MM = 37.3;
  const BARCODE_LABEL_H_MM = 25.9;

  const clampBarcodeQuantity = (value) => {
    const n = Math.floor(Number(value));
    if (!Number.isFinite(n) || n < 1) return 1;
    // Protect UI/perf; printing 1000+ barcodes could freeze the browser.
    return Math.min(n, 200);
  };

  const totalCopies = clampBarcodeQuantity(barcodeQuantity);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCopies / BARCODE_PER_PAGE)),
    [totalCopies]
  );

  // Filter options
  const statusOptions = [
    { id: null, name: "All Status" },
    { id: true, name: "Active" },
    { id: false, name: "Inactive" },
  ];

  const displayTopOptions = [
    { id: null, name: "All" },
    { id: true, name: "Display on Top" },
    { id: false, name: "Don't Display on Top" },
  ];

  const displayAppOptions = [
    { id: null, name: "All" },
    { id: true, name: "Display on App" },
    { id: false, name: "Don't Display on App" },
  ];

  useEffect(() => {
    getData(1);
    getCategories();
  }, []);

  useEffect(() => {
    getData(1);
  }, [categoryid, isactive, isdisplayontop, isdisplayonapp]);

  useEffect(() => {
    if (!open && refId) {
      setRefId(null);
    }
  }, [open]);

  const resetFilters = () => {
    setCategoryid(null);
    setIsactive(null);
    setIsdisplayontop(null);
    setIsdisplayonapp(null);
  };

  const getCategories = async () => {
    const response = await Common.ApiService.getInstance().request(
      "GetProductCategories"
    );
    if (response?.status === respEnum.Success) {
      const categoryOptions = response.data.map((cat) => ({
        value: cat.id,
        label: cat.name,
      }));
      setCategoriesList([
        { value: null, label: "All Categories" },
        ...categoryOptions,
      ]);
    }
  };

  const getData = async (
    pageNumber,
    searchTerm = "",
    recordsToDisplay = null
  ) => {
    const response = await Common.ApiService.getInstance().request(
      "GetProducts",
      {
        pageNumber: pageNumber,
        q: searchTerm,
        categoryid: categoryid,
        isactive: isactive,
        isdisplayontop: isdisplayontop,
        isdisplayonapp: isdisplayonapp,
        recordsToDisplay,
      },
      "POST"
    );

    if (response?.status === respEnum.Success && response?.data?.[0]) {
      setData(response.data[0]);
    }
  };

  const _handleDelete = async (id) => {
    const isConfirmed = await Common.SweetAlert.confirmation(
      "Are you sure you want to delete this Product?",
      "Delete Product"
    );
    if (isConfirmed) {
      let resp = await Common.ApiService.getInstance().request(
        `DeleteProduct?id=${id}`,
        {},
        "Delete"
      );

      if (resp.status === respEnum.Success) {
        Common.SweetAlert.alert(resp.message, "success");
        setTimeout(() => {
          getData(1);
        }, 500);
      } else {
        Common.SweetAlert.alert(
          resp.message || "Failed to delete product",
          "error"
        );
      }
    }
  };

  const _addUpdateTrigger = () => (
    <Button size="icon" variant="outline" color="secondary" className="h-7 w-7">
      <Icon icon="heroicons:pencil" className="h-4 w-4" />
    </Button>
  );

  const _onEdit = async (id) => {
    setOpen(true);
    setRefId(id);
  };

  const actions = [
    {
      type: "edit",
      onClick: (val) => _onEdit(val.id),
    },
    {
      type: "delete",
      onClick: (val) => _handleDelete(val.id),
    },
  ];

  const openBarcodePrint = (barcode, productName) => {
    const b = `${barcode || ""}`.trim();
    if (!b) {
      Common.showToast("Barcode is missing for this product", "Error", "info");
      return;
    }
    setSelectedBarcode(b);
    setSelectedProductName(`${productName || ""}`.trim());
    setBarcodeQuantity(1);
    setDidClickPrint(false);
    setBarcodeModalOpen(true);
  };

  const _thead = [
    { title: "Id", type: "number", isVisible: false, key: "id" },
    {
      title: "Product Name",
      type: "text",
      isVisible: true,
      key: "name",
    },
    { title: "BarCode", type: "text", isVisible: true, key: "barcode" },
    
    {
      title: "Description",
      type: "text",
      isVisible: true,
      key: "desc",
      render: (row) => (
        <div className="max-w-xs truncate" title={row.desc}>
          {row.desc || "N/A"}
        </div>
      ),
    },
    {
      title: "Category",
      type: "text",
      isVisible: true,
      key: "categoryName",
      render: (row) => row.categoryName || "N/A",
    },

    {
      title: "Regular Price",
      type: "text",
      isVisible: true,
      key: "regular_price",
      render: (row) => (
        <span className="font-medium">
          Rs. {row.regular_price?.toLocaleString() || "0"}
        </span>
      ),
    },
    {
      title: "Special Price",
      type: "text",
      isVisible: true,
      key: "special_price",
      render: (row) => (
        <span className="font-medium text-green-600">
          {row.special_price
            ? `Rs. ${row.special_price.toLocaleString()}`
            : "N/A"}
        </span>
      ),
    },
    {
      title: "Stock",
      type: "text",
      isVisible: true,
      key: "current_stock",
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            row.current_stock <= 10
              ? "bg-red-100 text-red-800"
              : "bg-green-100 text-green-800"
          }`}
        >
          {row.current_stock}
          {row.current_stock <= 10 && " (Low)"}
        </span>
      ),
    },
    {
      title: "Display on Top",
      type: "text",
      isVisible: true,
      key: "isdisplayontop",
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            row.isdisplayontop
              ? "bg-purple-100 text-purple-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {row.isdisplayontop ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: "Display on App",
      type: "text",
      isVisible: false,
      key: "isdisplayonapp",
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            row.isdisplayonapp
              ? "bg-blue-100 text-blue-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {row.isdisplayonapp ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: "Status",
      type: "text",
      isVisible: true,
      key: "isactive",
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            row.isactive
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {row.isactive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Print Barcode",
      type: "text",
      isVisible: true,
      key: "print_barcode",
      render: (row) => (
        <Button
          size="sm"
          variant="outline"
          className="h-7"
          onClick={() => openBarcodePrint(row?.barcode, row?.name)}
        >
          Print
        </Button>
      ),
    },
  ];

  return (
    <>
      <Dialog
        open={barcodeModalOpen}
        onOpenChange={(open) => {
          // If user closes/cancels without clicking Print, refresh product list.
          if (!open) {
            if (!didClickPrint) {
              getData(1);
            }

            setSelectedBarcode(null);
            setSelectedProductName("");
            setBarcodeQuantity(1);
            setDidClickPrint(false);
          }

          setBarcodeModalOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Print Barcode</DialogTitle>
          </DialogHeader>

          <style>
            {`
              @page {
                size: A4;
                margin: 10mm;
              }

              @media print {
                body * { visibility: hidden; }
                #barcodePrintArea, #barcodePrintArea * { visibility: visible; }
                #barcodePrintArea {
                  position: relative;
                  width: 100%;
                  margin: 0;
                  padding: 0;
                }
                #barcodePrintInner {
                  width: 100%;
                  max-width: 100%;
                  margin: 0;
                  padding: 0;
                }

                .barcode-page {
                  page-break-after: always;
                }

                .barcode-page:last-child {
                  page-break-after: auto;
                }

                /* Lock printed label size (mm) */
                .barcode-label {
                  width: ${BARCODE_LABEL_W_MM}mm;
                  height: ${BARCODE_LABEL_H_MM}mm;
                  border: 1px solid #111;
                  border-radius: 2mm;
                  padding: 1.5mm;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  overflow: hidden;
                  background: #fff;
                }

                .barcode-label svg {
                  width: 100% !important;
                  height: 100% !important;
                  display: block;
                }

                .barcode-col {
                  display: flex;
                  flex-direction: column;
                  gap: 3mm;
                }
              }
            `}
          </style>

          <div className="space-y-3">
            <div className="flex items-end justify-between gap-4">
              <div className="text-xs text-gray-600">
                Barcode:{" "}
                <span className="font-semibold">{selectedBarcode || "--"}</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="text-xs text-gray-600 whitespace-nowrap">
                  Quantity
                </div>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={barcodeQuantity}
                  onChange={(e) =>
                    setBarcodeQuantity(clampBarcodeQuantity(e.target.value))
                  }
                  className="w-24 h-8 border rounded-md px-2 text-sm"
                />
                <div className="text-xs text-gray-500 whitespace-nowrap">
                  A4 pages: {totalPages}
                </div>
              </div>
            </div>

            <div id="barcodePrintArea" className="space-y-3">
              <div id="barcodePrintInner" className="space-y-3">
                {Array.from({ length: totalPages }).map((_, pageIdx) => {
                  const pageStartIdx = pageIdx * BARCODE_PER_PAGE;
                  const leftStartIdx = pageStartIdx;
                  const rightStartIdx = pageStartIdx + ROWS_PER_COLUMN;

                  const leftIndices = Array.from(
                    { length: ROWS_PER_COLUMN },
                    (_, r) => leftStartIdx + r
                  ).filter((idx) => idx < totalCopies);

                  const rightIndices = Array.from(
                    { length: ROWS_PER_COLUMN },
                    (_, r) => rightStartIdx + r
                  ).filter((idx) => idx < totalCopies);

                  return (
                    <div key={`page-${pageIdx}`} className="barcode-page">
                      <div className="grid grid-cols-2 gap-4">
                        {/* Left column */}
                        <div className="barcode-col">
                          {leftIndices.map((idx) => (
                            <div
                              key={`left-${idx}`}
                              className="barcode-label"
                            >
                              {selectedBarcode ? (
                                <Barcode
                                  value={selectedBarcode}
                                  format="CODE128"
                                  displayValue
                                  background="#ffffff"
                                  lineColor="#000000"
                                  width={1.4}
                                  height={52}
                                  margin={0}
                                  fontSize={10}
                                />
                              ) : null}
                            </div>
                          ))}
                        </div>

                        {/* Right column */}
                        <div className="barcode-col">
                          {rightIndices.map((idx) => (
                            <div
                              key={`right-${idx}`}
                              className="barcode-label"
                            >
                              {selectedBarcode ? (
                                <Barcode
                                  value={selectedBarcode}
                                  format="CODE128"
                                  displayValue
                                  background="#ffffff"
                                  lineColor="#000000"
                                  width={1.4}
                                  height={52}
                                  margin={0}
                                  fontSize={10}
                                />
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDidClickPrint(false);
                setBarcodeModalOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setDidClickPrint(true);
                const originalTitle = document.title;
                const nextTitleRaw =
                  selectedProductName ||
                  selectedBarcode ||
                  "Barcodes";
                const nextTitle = `${nextTitleRaw}`.trim() || "Barcodes";
                // Windows/PDF exporters don't like invalid characters in titles.
                document.title = nextTitle.replace(/[\\/:*?"<>|]/g, "-");

                const handler = () => {
                  document.title = originalTitle;
                  window.removeEventListener("afterprint", handler);
                };
                window.addEventListener("afterprint", handler);

                window.print();
              }}
              disabled={!selectedBarcode}
            >
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4 p-4 bg-white rounded-lg shadow-sm border">
        <AppSearchSectionSelect
          value={
            categoriesList.find((cat) => cat.value === categoryid) ||
            categoriesList[0]
          }
          onChange={(value) => setCategoryid(value?.value)}
          label="Category"
          data={categoriesList}
          className="col-span-1"
        />

        <AppSelect
          value={isactive}
          onChange={setIsactive}
          label="Status"
          data={statusOptions}
          className="col-span-1"
        />

        <AppSelect
          value={isdisplayontop}
          onChange={setIsdisplayontop}
          label="Display on Top"
          data={displayTopOptions}
          className="col-span-1"
        />

        <AppSelect
          value={isdisplayonapp}
          onChange={setIsdisplayonapp}
          label="Display on App"
          data={displayAppOptions}
          className="col-span-1"
        />

        <Button
          className="mt-6 h-8 col-span-1"
          onClick={resetFilters}
          variant="outline"
        >
          Reset Filters
        </Button>
      </div>

      {data?.data && (
        <AdvanceTable
          title={"Products"}
          columns={_thead.filter((item) => item.isVisible)}
          data={data}
          getData={getData}
          search={SearchTypeEnum.Server_Search}
          searchInputClassName="custom-search-mobile "
          addButton={() => (
            <AddProduct
              id={refId}
              onClose={() => getData(1)}
              open={open}
              setOpen={setOpen}
            />
          )}
          showPagination={true}
          showAddButton
          actionButtons={actions}
          updateTrigger={_addUpdateTrigger}
        />
      )}
    </>
  );
};

export default Products;
